// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useAuth } from '../shared/context/AuthContext';

/**
 * @param {{
 *   user: import('firebase/auth').User,
 *   onEnrolled: () => void,
 * }} props
 */
export default function MfaEnrollmentScreen({ user, onEnrolled }) {
  const { signOut } = useAuth();
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialising, setInitialising] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasRegeneratedQr, setHasRegeneratedQr] = useState(false);

  const secretRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const session = await multiFactor(user).getSession();
        const secret = await TotpMultiFactorGenerator.generateSecret(session);
        if (cancelled) return;
        secretRef.current = secret;
        setQrUrl(secret.generateQrCodeUrl(user.email, 'Replico'));
      } catch (err) {
        if (!cancelled) setError('Failed to initialise MFA setup. Please refresh and try again.');
      } finally {
        if (!cancelled) setInitialising(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [user]);

  const handleEnroll = async () => {
    if (code.length !== 6 || !secretRef.current || loading) return;
    setLoading(true);
    setError('');
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secretRef.current, code);
      await multiFactor(user).enroll(assertion, 'TOTP');
      await user.reload();
      onEnrolled();
    } catch (err) {
      const errCode = /** @type {any} */ (err)?.code;
      if (errCode === 'auth/invalid-verification-code') {
        setError('Invalid code. Please check your authenticator app and try again.');
        // Firebase invalidates the enrollment session after a failed attempt.
        // Regenerate so the next submission has a fresh session and secret.
        setIsRegenerating(true);
        try {
          const session = await multiFactor(user).getSession();
          const newSecret = await TotpMultiFactorGenerator.generateSecret(session);
          secretRef.current = newSecret;
          setQrUrl(newSecret.generateQrCodeUrl(user.email, 'Replico'));
          setHasRegeneratedQr(true);
          setCode('');
        } catch {
          setError('Something went wrong. Please refresh and try again.');
        } finally {
          setIsRegenerating(false);
        }
      } else {
        setError('Something went wrong. Please refresh and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = code.length !== 6 || loading || isRegenerating;

  const handleSignOut = () => signOut();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={4} sx={{ p: 4, width: '100%', maxWidth: 440 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" gutterBottom>Set up two-factor authentication</Typography>
            <Typography variant="body2" color="text.secondary">
              Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to confirm.
            </Typography>
          </Box>

          {(initialising || isRegenerating) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
              <CircularProgress />
              {isRegenerating && (
                <Typography variant="body2" color="text.secondary">Generating new QR code…</Typography>
              )}
            </Box>
          )}

          {!initialising && !isRegenerating && qrUrl && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <QRCodeSVG value={qrUrl} size={200} />
              </Box>
              {hasRegeneratedQr && (
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  A new QR code has been generated. Delete the previous entry from your authenticator app and scan this new code.
                </Alert>
              )}
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                Do not close this window until you've confirmed the 6-digit code.
              </Alert>
            </>
          )}

          {!initialising && (
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEnroll(); }}
              error={!!error}
              helperText={error}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              disabled={isRegenerating}
              fullWidth
              autoFocus
            />
          )}

          {!initialising && (
            <Button
              onClick={handleEnroll}
              variant="contained"
              disabled={isSubmitDisabled}
              fullWidth
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify and activate'}
            </Button>
          )}

          {!initialising && (
            <Button variant="text" onClick={handleSignOut} disabled={loading || isRegenerating} fullWidth>
              Sign out
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
